import { EntryCriteriaTypes } from '@celito.clients/enums';
import {
  Icon,
  IconButton,
  InHouseInputSelect,
  TextField,
} from '@celito.clients/shared';
import { Divider, mergeClasses } from '@fluentui/react-components';
import { returnSelectedOptionFromOptions } from 'libs/core/src/utils/getSelectedOptionFromOptions';
import { IOption } from 'libs/shared/src/lib/in-house-input-select/in-house-input-select.model';
import { Fragment } from 'react/jsx-runtime';
import { Controller, useFormContext } from 'react-hook-form';

import { ComponentActions } from '../../../../../apps/web-client/src/screens/Platform/admin-ui-screen/admin-ui-object/tabs/admin-ui-lifecycle/admin-ui-lifecycle-details/components/admin-ui-lifecycle-details-stages/components/stage-properties/components/component-actions/component-actions.component';
import {
  CriteriaConditionType,
  CriteriaConditionTypeProps,
  CriteriaConfig,
} from './criteria-condition-type.model';
import { EntryCriteriasStyles } from './criteria-condition-type.styles';
import { StateOfRelatedObjects } from './stage-of-related-objects/state-of-related-objects.component';

interface CriteriaConditionTypeViewProps extends CriteriaConditionTypeProps {
  onAddEntryCriteria: () => void;
  handleConditionType: (conditionType: CriteriaConditionType) => void;
  onRemoveEntryCriteria: (index: number) => void;
  entryCriteriaTypes: IOption[];
  conditionTypes: IOption[];
}

export const CriteriaConditionTypeView = ({
  objectOptions,
  formPath,
  entryCriteriaTypes,
  conditionTypes,
  objectName,
  actionsOptions,
  IsLoadingActionData = false,
  onAddEntryCriteria,
  handleConditionType,
  onRemoveEntryCriteria,
}: CriteriaConditionTypeViewProps) => {
  const styles = EntryCriteriasStyles();

  const methods = useFormContext();

  const { watch, control } = methods;

  const watchedCriteria: CriteriaConfig[] = watch(`${formPath}.criteria`);
  const watchedConditionType = watch(`${formPath}.conditionType`);

  return (
    <div className={styles.formContainer}>
      <Controller
        name={`${formPath}.conditionType`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InHouseInputSelect
            label={'Condition Type'}
            options={conditionTypes}
            isDropdownOnly
            selectedOptions={returnSelectedOptionFromOptions(
              conditionTypes,
              field.value
            )}
            onOptionSelect={(e, data) =>
              handleConditionType(data.optionValue as CriteriaConditionType)
            }
          />
        )}
      />

      {watchedConditionType === CriteriaConditionType.When && (
        <div>
          <ComponentActions
            objectOptions={objectOptions}
            formPath={`${formPath}.performOnConditions`}
            IsLoadingActionData={IsLoadingActionData}
            actionsOptions={actionsOptions}
            hideActions
            ruleLabel="Criteria"
            isSingleLayout
            allowDeleteAllGroups={false}
          />

          <Divider alignContent="start">Set</Divider>
        </div>
      )}

      {watchedConditionType &&
        watchedCriteria?.map((criteria, index) => {
          const watchedType = watch(`${formPath}.criteria.${index}.type`);

          return (
            <Fragment key={`criteira-${criteria.validations.object}-${index}`}>
              <div className={styles.criteriaContainer}>
                <IconButton
                  onClick={() => onRemoveEntryCriteria(index)}
                  className={mergeClasses(
                    styles.deleteBtn,
                    index === 0 &&
                      watchedCriteria.length === 1 &&
                      styles.visibilityOff
                  )}
                  icon={<Icon iconName="Delete20Regular" />}
                />

                <Controller
                  name={`${formPath}.criteria.${index}.type`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <InHouseInputSelect
                      label={'Type'}
                      options={entryCriteriaTypes}
                      selectedOptions={returnSelectedOptionFromOptions(
                        entryCriteriaTypes,
                        field.value
                      )}
                      onOptionSelect={(e, data) => {
                        field.onChange(data.optionValue);
                      }}
                      errorMessage={error?.message}
                      required
                    />
                  )}
                />

                {watchedType ===
                  EntryCriteriaTypes['State Of Related Objects'] && (
                  <StateOfRelatedObjects
                    index={index}
                    objectName={objectName}
                    objectOptions={objectOptions}
                    formPath={`${formPath}.criteria`}
                    criteria={criteria}
                  />
                )}

                <Divider />

                <Controller
                  name={`${formPath}.criteria.${index}.errorMessage`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Error Message"
                      value={field.value}
                      errorMessage={error?.message}
                      onChange={(_, data) => field.onChange(data.value)}
                    />
                  )}
                />
              </div>

              {watchedCriteria.length > 1 &&
                index < watchedCriteria.length - 1 && (
                  <Divider alignContent="start">AND</Divider>
                )}
            </Fragment>
          );
        })}

      <Divider />

      {watchedConditionType !== CriteriaConditionType.Never && (
        <button
          onClick={(e) => {
            e.preventDefault();
            onAddEntryCriteria();
          }}
          className={styles.btnAddCriteria}
          data-testid={`button-add-choice`}
        >
          + Add Criteria
        </button>
      )}
    </div>
  );
};
